import React, { lazy, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FeedbackContainer, ToastContainer, useDeviceDetection } from '@gafe/common-ui';
import * as Sentry from '@sentry/react';
import merge from 'classnames';
import { last } from 'lodash';

import { AssistingBanner } from '@/components/generic/AssistingBanner';
import { Footer } from '@/components/layout/Footer';
import { Header } from '@/components/layout/Header';
import { AcceptLeadsPassingModal } from '@/components/modals/AcceptLeadsPassing';
import { SubmitOrderModal } from '@/components/modals/SubmitOrder';
import { TrainingRequiredModal } from '@/components/modals/TrainingRequired';
import { MainLoader } from '@/components/shared/MainLoader';
import { useAuth } from '@/hooks/useAuth';
import { useHeartbeat } from '@/hooks/useHeartbeat/useHeartbeat';
import HealthCheck from '@/pages/Public/HealthCheck';
import ContactSupportPage from '@/pages/System/ContactSupport';
import { InactivityLogoutPage } from '@/pages/System/InactivityLogout';
import UnexpectedErrorPage from '@/pages/System/UnexpectedError';
import { Router, ROUTES } from '@/router';
import { BrowserStorageService } from '@/services/BrowserStorage';
import { CustomExceptions, MediaType } from '@/types';

import { Props } from './types';

import styles from './Content.module.scss';

// define outside component route to avoid ES lint complaining about it
const siteRoute = ROUTES.Private.siteSurvey.segment;
const proposalPricing = ROUTES.Private.ProposalPricing.segment;

const publicAuthRoutes = [ROUTES.Auth.LogIn, ROUTES.Auth.ForgotPassword];
const SiteSurveyProvider = lazy(() => import('@gafe-shared/SiteSurveyTask/SiteSurveyProvider'));

export const Content = ({ initialiseUploadingQueue, globalError }: Props) => {
  const history = useHistory();
  const location = useLocation();
  const mediaType = useDeviceDetection();
  const { logout } = useAuth();

  const isSiteSurvey = location.pathname.includes(siteRoute);
  const isProposalPricingPage = location.pathname.includes(proposalPricing);
  const isPublicAuthRoute = publicAuthRoutes.includes(location.pathname);

  const isDashboard = /^\/dashboard$/.test(location.pathname);
  const containerClass = merge({
    [styles.content]: !isSiteSurvey,
    [styles.dashboard]: isDashboard,
    [styles.fullscreen]: isPublicAuthRoute || isProposalPricingPage,
  });

  useHeartbeat({
    onDeath: logout,
  });

  useEffect(() => {
    const preventDragEvent = (event: DragEvent) => event.preventDefault();

    initialiseUploadingQueue();

    window.addEventListener('dragover', preventDragEvent);
    window.addEventListener('drop', preventDragEvent);

    return () => {
      window.removeEventListener('dragover', preventDragEvent);
      window.removeEventListener('drop', preventDragEvent);
    };
  }, [initialiseUploadingQueue]);

  const showGlobalFooter = useMemo(() => {
    const pathname = history.location.pathname;

    const routesToHide = [
      ROUTES.Auth.Welcome,
      ROUTES.Private.siteSurvey.segment,
      last(ROUTES.Private.siteSurvey.submitSurvey.split('/')),
    ];

    return !pathname.split('/').find(routeName => routesToHide.includes(routeName));
  }, [history.location.pathname]);

  const addBottomGapToFooter =
    mediaType === MediaType.Mobile &&
    history.location.pathname === ROUTES.Private.Dashboard.ActivityList;

  if (history.location.pathname === ROUTES.Public.HealthCheck) {
    return <HealthCheck />;
  }

  if (BrowserStorageService.loggedOutDueToInactivity.get()) {
    return <InactivityLogoutPage />;
  }

  return (
    <React.Suspense fallback={<MainLoader />}>
      <AssistingBanner />
      {!isPublicAuthRoute && <Header />}
      <FeedbackContainer offset="md" />
      <ToastContainer />
      <main className={containerClass} id="main_content_container">
        <Sentry.ErrorBoundary fallback={<UnexpectedErrorPage />}>
          {globalError?.message === CustomExceptions.BadUserInfo ? (
            <ContactSupportPage />
          ) : (
            <SiteSurveyProvider>
              <Router />
            </SiteSurveyProvider>
          )}
        </Sentry.ErrorBoundary>
      </main>
      {showGlobalFooter && (
        <Footer
          bottomGap={addBottomGapToFooter}
          classes={{ wrapper: merge({ [styles.auth_footer]: isPublicAuthRoute }) }}
        />
      )}
      <AcceptLeadsPassingModal />
      <SubmitOrderModal />
      <TrainingRequiredModal />
    </React.Suspense>
  );
};
